import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { EMAILS } from '@app/constants';
import { buildMissingDiseaseMessage, copyText } from '@app/utils';
import { useSubmitContactForm } from '@data/hooks';
import { ContactFormTopic } from '@data/sdk/match';
import { useDiseaseSearchAnalytics } from '../../../hooks/useAnalytics';
import { ContactSupportFormValues, ContactSupportView } from '../types';

export const useContactSupport = (enabledAnalytics: boolean) => {
  const [view, setView] = useState<ContactSupportView>('collapsed');
  const [sentTo, setSentTo] = useState('');
  const [lastValues, setLastValues] = useState<ContactSupportFormValues>();
  const [isEmailCopied, setIsEmailCopied] = useState(false);
  const { mutate, isPending } = useSubmitContactForm();
  const {
    trackDiseaseSearchContactOpened,
    trackDiseaseSearchContactSent,
    trackDiseaseSearchContactEmailCopied,
  } = useDiseaseSearchAnalytics(enabledAnalytics);

  const handleOpen = () => {
    setView('form');
    trackDiseaseSearchContactOpened();
  };

  const handleRetry = () => setView('form');

  const handleCancel = () => setView('collapsed');

  const handleCopyEmail = async () => {
    try {
      await copyText(EMAILS.PATIENTS_CUREWIKI_HEALTH);
    } catch (error) {
      Sentry.captureException(error);
      return;
    }
    setIsEmailCopied(true);
    setTimeout(() => setIsEmailCopied(false), 2000);
    trackDiseaseSearchContactEmailCopied();
  };

  const handleRecaptchaError = (error: unknown, values: ContactSupportFormValues) => {
    Sentry.captureException(error);
    setLastValues(values);
    setView('error');
  };

  const handleSubmit = (
    { email, searchTerm, synonyms }: ContactSupportFormValues,
    recaptchaToken: string,
  ) => {
    setLastValues({ email, searchTerm, synonyms });
    mutate(
      {
        email,
        topic: ContactFormTopic.MissingDisease,
        message: buildMissingDiseaseMessage({
          searchTerm,
          synonyms,
          page: window.location.pathname,
        }),
        recaptchaToken,
      },
      {
        onSuccess: () => {
          setSentTo(email);
          setView('sent');
          trackDiseaseSearchContactSent();
        },
        onError: () => setView('error'),
      },
    );
  };

  return {
    view,
    sentTo,
    isSubmitting: isPending,
    lastValues,
    handleOpen,
    handleRetry,
    handleCancel,
    handleSubmit,
    handleRecaptchaError,
    isEmailCopied,
    handleCopyEmail,
  };
};
